import { Button, Result, Table } from 'antd'
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { AssignmentSections } from '../../../../../../constants/assignment-sections'
import {
    FLAGGED_PARAM,
    FOCUS_QUESTION_ID_PARAM,
    OPEN_SUBMISSION_ID_PARAM,
} from '../../../../../../constants/query-params'
import useQueryParamSetter from '../../../../../../hooks/useQueryParamSetter'
import { useFetchQuestionsQuery } from '../../../../../../store/apis/question.api'
import { useFetchEscalatedQuestionAnswersQuery } from '../../../../../../store/apis/submission.api'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import Routes from '../../../../../../constants/routes'
import './escalated-submissions-table.scss'
import { FlagOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { getQuestionById } from '../../../../../../store/apis/utils/selectors/question.api.selectors'
import useSmartPagination from './useSmartPagination'
import AssigneeSelect from './AssigneeSelect'
import User from '../../../../../../components/common/user/User'

export const escalatedStatuses = {
    manualUnresolved: 1,
    unexplainableUnresolved: 3,
}

const EscalatedSubmissionsTable = ({ filtersState }) => {
    const { token } = useParams()
    const versionId = useVersionId()
    const setQueryParam = useQueryParamSetter()
    const navigate = useNavigate()

    const { data: questions, isFetching: isQuestionsFetching } = useFetchQuestionsQuery({ token, versionId })

    const {
        data: escalatedAnswersPerQuestion,
        isFetching,
        error,
        refetch,
    } = useFetchEscalatedQuestionAnswersQuery({ token, versionId })

    const onSelectSubmission = (submissionId, questionRubricId) => {
        setQueryParam({
            [FLAGGED_PARAM]: 'true',
            [OPEN_SUBMISSION_ID_PARAM]: submissionId,
            [FOCUS_QUESTION_ID_PARAM]: questionRubricId,
        })
        navigate({
            pathname: `${Routes.ASSIGNMENTS}/${token}/all/${AssignmentSections.HOME}`,
            search: location.search,
        })
    }

    const columns = [
        {
            title: 'Student ID',
            dataIndex: 'studentId',
            key: 'studentId',
            render: (studentId, escalatedSubmission) => (
                <a
                    type='link'
                    onClick={() => onSelectSubmission(escalatedSubmission.submissionId, escalatedSubmission.questionRubricId)}
                    data-test='escalated-submission-link'
                >
                    {studentId}
                </a>
            ),
            sorter: {
                compare: (a, b) => (a.studentId > b.studentId ? 1 : a.studentId < b.studentId ? -1 : 0),
                multiple: 1,
            },
            defaultSortOrder: 'ascend',
        },
        {
            title: 'Question',
            dataIndex: 'questionName',
            key: 'questionName',
            sorter: { compare: (a, b) => a.questionOrder - b.questionOrder, multiple: 2 },
            defaultSortOrder: 'ascend',
        },
        {
            title: 'Rubric',
            dataIndex: 'rubricName',
            key: 'rubricName',
            sorter: { compare: (a, b) => (a.rubricName || '').localeCompare(b.rubricName || ''), multiple: 3 },
        },
        {
            title: 'Type',
            dataIndex: 'status',
            key: 'status',
            width: 50,
            render: status =>
                status === escalatedStatuses.manualUnresolved ? (
                    <FlagOutlined style={{ fontSize: 16 }} title="Flagged by user" />
                ) : (
                    <QuestionCircleOutlined style={{ fontSize: 16 }} title="Unexplainable" />
                ),
        },
        {
            title: 'Assignee',
            dataIndex: 'user',
            key: 'user',
            render: (user, escalatedSubmission) => (
                <AssigneeSelect
                    escalatedSubmission={escalatedSubmission}
                    currentAssigneeId={user?.id}
                />
            ),
            sorter: {
                compare: (a, b) => {
                    const nameA = a.user?.name ?? ''
                    const nameB = b.user?.name ?? ''
                    return nameA.localeCompare(nameB)
                },
                multiple: 4,
            },
        },
        {
            title: 'Escalated By',
            dataIndex: 'escalatedBy',
            key: 'escalatedBy',
            render: (escalatedBy) => (
                escalatedBy ? <User id={escalatedBy.id} name={escalatedBy.name} size="small" /> : null
            ),
            sorter: {
                compare: (a, b) => {
                    const nameA = a.escalatedBy?.name ?? ''
                    const nameB = b.escalatedBy?.name ?? ''
                    return nameA.localeCompare(nameB)
                },
                multiple: 5,
            },
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
        },
    ]

    const data = useMemo(() => {
        if (!escalatedAnswersPerQuestion) {
            return []
        }

        let allEscalatedAnswers = []

        for (const questionId of Object.keys(escalatedAnswersPerQuestion)) {
            const question = getQuestionById(questions, Number(questionId))

            allEscalatedAnswers = allEscalatedAnswers.concat(
                escalatedAnswersPerQuestion[questionId].map(answer => {
                    // Find the rubric name from the question's questionRubrics
                    const questionRubric = question?.questionRubrics?.find(qr => qr.id === answer.questionRubricId)
                    
                    return {
                        ...answer,
                        questionName: question?.name,
                        questionOrder: question?.order,
                        rubricName: questionRubric?.name,
                    }
                }),
            )
        }

        const statusesToDisplay = Object.keys(filtersState)
            .filter(key => filtersState[key])
            .map(Number)

        if (statusesToDisplay.length) {
            allEscalatedAnswers = allEscalatedAnswers.filter(answer => statusesToDisplay.includes(answer.status))
        }

        return allEscalatedAnswers
    }, [escalatedAnswersPerQuestion, questions, filtersState])

    // Smart pagination hook
    const storageKey = `escalated-submissions-pagesize-${token}-${versionId}`
    const { paginationConfig, handlePageSizeChange } = useSmartPagination(data.length, storageKey)

    // Handle table changes (pagination, sorting, filtering)
    const handleTableChange = (pagination) => {
        if (pagination.pageSize) {
            handlePageSizeChange(pagination.pageSize)
        }
    }

    return error ? (
        <Result
            status='error'
            title='Escalated Submissions Fetch Failed'
            subTitle={error}
            extra={[
                <Button key='try' onClick={() => refetch()} loading={isFetching || isQuestionsFetching}>
                    Try Again
                </Button>,
            ]}
        />
    ) : (
        <Table
            size='small'
            columns={columns}
            dataSource={data}
            pagination={paginationConfig}
            onChange={handleTableChange}
            rowClassName={'test-class'}
            loading={{ spinning: isFetching || isQuestionsFetching, tip: 'Loading escalated submissions...' }}
            data-test='escalated-table'
        />
    )
}

export default EscalatedSubmissionsTable
